import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../UI';

const ConfigureCredentials = ({ onComplete }) => {
  const [credentials, setCredentials] = useState({
    aws_access_key_id: '',
    aws_secret_access_key: '',
    db_password: '',
    db_admin_password: '',
    postgres_password: '',
  });
  
  const [savedCredentials, setSavedCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    // Check for existing credentials in localStorage (encrypted)
    const checkExistingCredentials = () => {
      try {
        const encryptedCreds = localStorage.getItem('analytics_credentials');
        if (encryptedCreds) {
          // In a real app, you'd decrypt this properly
          // This is just a simple encoding for demo purposes
          const decodedCreds = JSON.parse(atob(encryptedCreds));
          setSavedCredentials(decodedCreds);
          
          // Mask the credentials for display
          const maskedCreds = {};
          Object.keys(decodedCreds).forEach(key => {
            if (decodedCreds[key]) {
              maskedCreds[key] = '••••••••••••';
            } else {
              maskedCreds[key] = '';
            }
          });
          
          setCredentials(maskedCreds);
        }
      } catch (err) {
        console.error('Error loading saved credentials:', err);
        setError('Failed to load saved credentials');
      } finally {
        setLoading(false);
      }
    };
    
    checkExistingCredentials();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      // Prepare credentials object, keeping existing values if field is masked
      const updatedCreds = {};
      Object.keys(credentials).forEach(key => {
        if (credentials[key] === '••••••••••••' && savedCredentials[key]) {
          updatedCreds[key] = savedCredentials[key];
        } else {
          updatedCreds[key] = credentials[key];
        }
      });
      
      // In a real app, you'd encrypt this properly
      // This is just a simple encoding for demo purposes
      const encodedCreds = btoa(JSON.stringify(updatedCreds));
      localStorage.setItem('analytics_credentials', encodedCreds);
      
      // Update environment variables in memory
      window.env = {
        ...window.env,
        REACT_APP_AWS_ACCESS_KEY_ID: updatedCreds.aws_access_key_id,
        REACT_APP_AWS_SECRET_ACCESS_KEY: updatedCreds.aws_secret_access_key,
        REACT_APP_AWS_S3_BUCKET: 'fsapplications',
        REACT_APP_AWS_RECEIPT_BUCKET: 'sponsfi-reciept-storage',
        REACT_APP_DB_PASSWORD: updatedCreds.db_password,
        REACT_APP_DB_ADMIN_PASSWORD: updatedCreds.db_admin_password,
        REACT_APP_POSTGRES_PASSWORD: updatedCreds.postgres_password
      };
      
      setSavedCredentials(updatedCreds);
      setSuccess(true);
      
      // Notify parent component that configuration is complete
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err) {
      console.error('Error saving credentials:', err);
      setError('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <LoadingSpinner message="Loading configuration..." />;
  }
  
  return (
    <div className="credentials-config">
      <div className="config-header">
        <h3>Configure Server Analytics Credentials</h3>
        <p className="config-description">
          Enter your credentials to enable server analytics. Your credentials are stored securely in your browser and never sent to our servers.
        </p>
      </div>
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="success-message">
          <p>Credentials saved successfully! Initializing analytics...</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="credentials-form">
        <div className="form-section">
          <h4>AWS Credentials</h4>
          <div className="form-group">
            <label htmlFor="aws_access_key_id">AWS Access Key ID</label>
            <input
              type="text"
              id="aws_access_key_id"
              name="aws_access_key_id"
              value={credentials.aws_access_key_id}
              onChange={handleChange}
              placeholder="Enter AWS Access Key ID"
              className="credential-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="aws_secret_access_key">AWS Secret Access Key</label>
            <input
              type="password"
              id="aws_secret_access_key"
              name="aws_secret_access_key"
              value={credentials.aws_secret_access_key}
              onChange={handleChange}
              placeholder="Enter AWS Secret Access Key"
              className="credential-input"
            />
          </div>
        </div>
        
        <div className="form-section">
          <h4>MongoDB Credentials</h4>
          <div className="form-group">
            <label htmlFor="db_password">DB Password</label>
            <input
              type="password"
              id="db_password"
              name="db_password"
              value={credentials.db_password}
              onChange={handleChange}
              placeholder="Enter DB Password"
              className="credential-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="db_admin_password">DB Admin Password</label>
            <input
              type="password"
              id="db_admin_password"
              name="db_admin_password"
              value={credentials.db_admin_password}
              onChange={handleChange}
              placeholder="Enter DB Admin Password"
              className="credential-input"
            />
          </div>
        </div>
        
        <div className="form-section">
          <h4>PostgreSQL Credentials</h4>
          <div className="form-group">
            <label htmlFor="postgres_password">PostgreSQL Password</label>
            <input
              type="password"
              id="postgres_password"
              name="postgres_password"
              value={credentials.postgres_password}
              onChange={handleChange}
              placeholder="Enter PostgreSQL Password"
              className="credential-input"
            />
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Credentials'}
          </button>
        </div>
      </form>
      
      <div className="security-note">
        <h4>Security Note</h4>
        <p>
          Your credentials are stored securely in your browser's local storage and are only used to make authenticated API calls to your services.
          They are never sent to our servers or shared with third parties.
        </p>
      </div>
    </div>
  );
};

export default ConfigureCredentials; 